import { Quill } from 'react-quill';

import { Base64 } from 'js-base64';

import { isRichTextEmpty } from './richTextUtils';


// Custom button icons
const icons = Quill.import('ui/icons');
icons['textColor'] = '<i class="fa-solid fa-palette" style="font-size: 14px;"></i>';

export const rgbStringToObject = (rgbString: string): { r: number, g: number, b: number } => {
    const match = rgbString.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (match) {
        return {
            r: parseInt(match[1]),
            g: parseInt(match[2]),
            b: parseInt(match[3])
        };
    }
    return { r: 0, g: 0, b: 0 };
};

export const rgbObjectToString = (rgb: { r: number, g: number, b: number }): string => {
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
};

export const formats = [
    'size',
    'header',
    'bold',
    'italic',
    'underline',
    'color',
    'link'
];

/**
 * Encodes rich text content to base64 for API storage
 * Also extracts and cleans color information
 */
export const processRichTextContent = (richText?: { text?: string; fontColor?: string }) => {
    if (!richText) return undefined;

    const content = richText?.text || '';
    const isEmpty = isRichTextEmpty(content);

    // If content is empty, always use black color
    if (isEmpty) {
        return {
            text: Base64.encode(content),
            fontColor: 'rgb(0, 0, 0)'
        };
    }

    // Extract color from HTML content (keep as RGB)
    const colorMatch = content.match(/color:\s*([^;"]+)/);
    const extractedColor = colorMatch ? colorMatch[1].trim() : (richText?.fontColor || 'rgb(0, 0, 0)');

    // Clean HTML by removing all color styles
    let cleanText = content.replace(/style="[^"]*"/g, (match) => {
        const cleanedStyle = match.replace(/color:[^;"]*;?/g, '').replace(/;\s*;/g, ';').replace(/style="[;\s]*"/g, '');
        return cleanedStyle === 'style=""' ? '' : cleanedStyle;
    });

    // Unwrap spans that only had color (no other attrs) so stored HTML stays normal
    cleanText = cleanText.replace(/<span(?:\s+[^>]*)?>([\s\S]*?)<\/span>/gi, (match, inner) => {
        const beforeBracket = match.slice(5, match.indexOf('>'));
        return beforeBracket.trim() === '' ? inner : match;
    });

    const encodedText = Base64.encode(cleanText);

    return {
        text: encodedText,
        fontColor: extractedColor
    };
};

/**
 * Decodes rich text content from base64 for editing
 * Returns decoded text or empty string if decoding fails
 */
export const decodeRichTextContent = (richText?: { text?: string; fontColor?: string }) => {
    if (!richText?.text) {
        return {
            text: '',
            fontColor: richText?.fontColor || 'rgb(0, 0, 0)'
        };
    }

    try {
        return {
            text: Base64.decode(richText.text),
            fontColor: richText.fontColor || 'rgb(0, 0, 0)'
        };
    } catch (error) {
        // If decoding fails, assume it's already decoded
        return {
            text: richText.text,
            fontColor: richText.fontColor || 'rgb(0, 0, 0)'
        };
    }
};

export const extractColorValue = (color: any): string => {
    if (typeof color === 'string') {
        return color;
    }
    if (color && typeof color === 'object' && color.colorOne) {
        return color.colorOne;
    }
    return '';
};

/**
 * Injects fontColor into HTML when content has no inline color.
 * Quill expects color as inline <span> (not on block elements), so we wrap
 * block content in <span style="color: ..."> for proper parsing.
 */
export const injectFontColorIntoHtml = (
    html: string,
    fontColor?: string
): string => {
    if (!html || !fontColor || fontColor === 'rgb(0, 0, 0)') return html;
    if (/color:\s*\S+/.test(html)) return html;

    const wrap = (content: string) =>
        content.trim() && !content.includes('<span')
            ? `<span style="color: ${fontColor}">${content}</span>`
            : content;

    return html.replace(
        /<(p|h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1>/g,
        (_, tag, attrs, content) => `<${tag}${attrs || ''}>${wrap(content)}</${tag}>`
    );
};
